import { useEffect, useRef } from 'react'
import { useQueue } from '../hooks/useQueue'
import type { OutputFormat } from '../types'
import type { DropZoneHandle } from './DropZone.tsx'
import DropZone from './DropZone.tsx'
import FormatSelector from './FormatSelector.tsx'
import QualitySelector from './QualitySelector.tsx'
import Queue from './Queue.tsx'
import DownloadBar from './DownloadBar.tsx'

interface WorkspaceProps {
  preset?: OutputFormat
  dropZoneRef?: React.RefObject<DropZoneHandle>
}

export default function Workspace({ preset = 'jpg', dropZoneRef }: WorkspaceProps) {
  const {
    items,
    format,
    quality,
    isConverting,
    announcement,
    rejected,
    addFiles,
    setFormat,
    setQuality,
    removeItem,
    retryItem,
    clearAll,
    startConversion,
    downloadItem,
    downloadAll,
  } = useQueue(preset)

  const statusRef = useRef<HTMLDivElement>(null)
  const convertButtonRef = useRef<HTMLButtonElement>(null)
  const previousItemCount = useRef(items.length)
  const wasConverting = useRef(isConverting)

  useEffect(() => {
    if (items.length > previousItemCount.current && items.length > 0 && !isConverting) {
      convertButtonRef.current?.focus()
    }
    previousItemCount.current = items.length
  }, [items, isConverting])

  useEffect(() => {
    if (isConverting && !wasConverting.current) {
      statusRef.current?.focus()
    }
    wasConverting.current = isConverting
  }, [isConverting])

  const completed = items.filter(
    (it) => it.status === 'succeeded' || it.status === 'failed' || it.status === 'cancelled',
  ).length
  const total = items.length
  const progressText =
    isConverting && total > 0
      ? `Converting ${completed + 1} of ${total}`
      : `${completed} of ${total} files processed`

  return (
    <section className="workspace" id="converter" aria-label="Conversion workspace">
      <div
        ref={statusRef}
        className="sr-only"
        aria-live="polite"
        id="workspace-status"
        tabIndex={-1}
      >
        {announcement}
      </div>

      {rejected.length > 0 && (
        <div className="rejected" role="status" aria-live="polite">
          <ul>
            {rejected.map((r, i) => (
              <li key={`${r.fileName}-${i}`}>
                <strong>{r.fileName}</strong>: {r.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="limits" aria-label="Conversion limits">
        <div><b>20 files max</b>Per batch</div>
        <div><b>30 MB each</b>File size limit</div>
        <div><b>12,000 px</b>Longest side</div>
        <div><b>One at a time</b>Processing order</div>
      </div>

      <DropZone ref={dropZoneRef} onFiles={addFiles} />

      <FormatSelector value={format} onChange={setFormat} />

      {format === 'jpg' && <QualitySelector value={quality} onChange={setQuality} />}

      {total > 0 && (
        <div className="progress" role="status" aria-label="Overall conversion progress">
          {progressText}
        </div>
      )}

      <Queue
        items={items}
        onRemove={removeItem}
        onDownload={downloadItem}
        onRetry={retryItem}
      />

      <DownloadBar
        ref={convertButtonRef}
        items={items}
        isConverting={isConverting}
        onConvert={startConversion}
        onDownloadAll={downloadAll}
      />

      {items.length > 0 && (
        <button className="clear" type="button" onClick={clearAll}>
          Clear all
        </button>
      )}
    </section>
  )
}
